import {
  BlockTitle,
  PageTitle,
  QAGroup,
  QAPair,
  StatusTag,
  Surface,
  titleCase,
} from '@westhetics/ui'
import { useParams } from 'react-router'
import { LoadError } from '@/app/load-error'
import { BackLink, ReadingSkeleton, noteText, supportText } from '@/app/shell'
import { formatDateTimeIn } from '@/lib/format'
import { routes } from '@/routes'
import { useAnamnesis } from './api'

/**
 * A ficha de saúde (docs/areas/portal-do-paciente.md, "Ficha de saúde"; RN-POR-07): a prova, os
 * dados confirmados e os blocos na ordem em que ela respondeu, em pares pergunta e resposta. Sem
 * bloco de atenção e sem sinalização: a leitura clínica é da profissional. Arquétipo: leitura.
 */

const LGPD_NOTE =
  'Suas respostas ficam com a profissional responsável pelo seu atendimento, protegidas pela LGPD. Para atualizar, responda a nova ficha quando a clínica enviar.'

export function AnamnesisPage() {
  const { id = '' } = useParams()
  const query = useAnamnesis(id)

  if (query.isPending) return <ReadingSkeleton label="Carregando a ficha de saúde" />
  if (query.isError) {
    return (
      <div className="flex flex-col gap-4">
        <BackLink to={routes.home}>Voltar ao início</BackLink>
        <LoadError
          title="Não conseguimos abrir esta ficha de saúde"
          error={query.error}
          onRetry={() => void query.refetch()}
        />
      </div>
    )
  }

  const a = query.data
  const completed = formatDateTimeIn(a.completedAt, a.clinic.timezone)
  const evidence = a.evidence
  const back = a.appointmentId
    ? { to: routes.appointment(a.appointmentId), label: 'Voltar ao atendimento' }
    : { to: routes.home, label: 'Voltar ao início' }

  return (
    <article aria-label="Ficha de saúde" className="flex flex-col gap-6">
      <BackLink to={back.to}>{back.label}</BackLink>
      <PageTitle
        eyebrow={`Ficha de saúde · ${a.clinic.name}`}
        title={`Ficha de saúde de ${completed.date}`}
        meta={[
          a.procedureAtSendTime ? `para ${a.procedureAtSendTime.name}` : null,
          a.practitioner ? `profissional ${titleCase(a.practitioner.name)}` : null,
        ]
          .filter(Boolean)
          .join(' · ')}
        trailing={<StatusTag level="done">Respondida</StatusTag>}
      />

      <Surface padding="sm" role="group" aria-label="Prova do envio">
        {evidence ? (
          <>
            <p className="font-body text-ink text-[13px] leading-[1.6] font-light">
              Enviada em {completed.date} · {completed.time} · Protocolo {evidence.protocolNumber}
            </p>
            <p className={`${noteText} mt-1`}>
              hash {evidence.shortHash}
              {evidence.device ? ` · respondida em ${evidence.device}` : ''}
            </p>
          </>
        ) : (
          <p className={supportText}>
            Ficha importada do sistema anterior: é lida com as perguntas de então e não tem
            protocolo nem hash.
          </p>
        )}
      </Surface>

      {a.sections.map((section) => (
        <QAGroup key={section.key} title={section.name} className="border-t-0 pt-0">
          <div className="tablet:grid-cols-2 grid grid-cols-1 gap-x-6">
            {section.questions.map((q, index) => (
              <QAPair
                key={`${section.key}:${index}`}
                question={q.shortLabel}
                answer={q.answerText}
                title={q.label}
              />
            ))}
          </div>
        </QAGroup>
      ))}

      {evidence?.declaration.text ? (
        <section aria-label="Declaração aceita" className="flex flex-col gap-3">
          <BlockTitle
            title="Declaração aceita"
            note={[
              evidence.declaration.version ? `versão ${evidence.declaration.version}` : null,
              evidence.declaration.acceptedAt
                ? `aceita em ${formatDateTimeIn(evidence.declaration.acceptedAt, a.clinic.timezone).date}`
                : null,
            ]
              .filter(Boolean)
              .join(' · ')}
          />
          <p className="font-body text-body text-ink font-light">{evidence.declaration.text}</p>
        </section>
      ) : null}

      <p className={noteText}>{LGPD_NOTE}</p>
    </article>
  )
}
